import axios from 'axios';
import { subMonths } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getAutoUpdatedMap } from 'shared/hooks/getAutoUpdatedMap.ts';
import { Badge } from 'features/catalog/useBadges.ts';

import { buildContributorIndex, getContributorIds } from 'features/catalog/badge-contributors.ts';
import { JOURNEYS, isJourneyCompleted } from 'features/catalog/badge-journeys.ts';
import { RESPONSES_TSV_URL } from 'config/data-sources';
import { getFeedUpdatesCutoffDate } from 'config/constants';
import { getFirstPartOfEmail } from 'shared/lib/email';
import { liveCatalogBadgeIDs } from 'features/catalog/badge-state';
import { parseSpreadsheetTimestamp } from 'shared/lib/spreadsheet-time';
import { accumulateOwnedBadgeIDs } from 'features/claim/clipboard';
import { parseBadgeString } from 'features/claim/parse-badge-string';
import { sortScoreForSheetBadgeIDs } from 'features/profiles/profile-score';
import { rankAmongProfilesExcluding } from 'features/profiles/rank-peers.ts';
import { compareJourneysByClaimScoreAsc, getRarityScoreFromName } from 'features/catalog/rarity';

/** Final Profile shape:
 * - `submittedBadgeIDs` is what the user originally submitted.
 * - `badgesIDs` is the final set of live-catalog badges (after auto-claim & restricted rules;
 *   retired/unknown sheet IDs are dropped).
 */
export interface Profile {
  email: string;
  name: string;
  /**
   * Last sheet submission timestamp — source of truth for claim “since last update”
   * and feed post-catalog grant stamping. Not bumped by auto-grants.
   */
  lastUpdated: Date;
  /**
   * Newest claim / join / grant activity (matches Profile Feed timestamps for those
   * cards). Catalog “contributed” drops still appear on the feed but do not bump this.
   * Defaults to `lastUpdated`; App stamps after feed derivation. Use for “updated …”.
   */
  lastActivityAt: Date;
  submittedBadgeIDs: string[]; // The raw user-submitted badges
  badgesIDs: string[]; // Live catalog badges after auto-updates (no retired/unknown)
  sortScore: number; // Summation of badge rarities + journey completions
  journeyEmojis: string[]; // Which journeys they completed
  /**
   * Only when 2+ submissions and rank improved since the baseline submission.
   * Baseline is the immediately-preceding submission, unless there are more than 2
   * submissions within the feed-updates cutoff window, in which case it's the
   * submission closest to one month ago.
   */
  positionsMovedUp?: number;
}

/** Comparator: descending by sortScore, then ascending by lastUpdated (earlier = lower rank). */
export const compareProfilesByScore = (a: Profile, b: Profile): number =>
  a.sortScore !== b.sortScore ? b.sortScore - a.sortScore : a.lastUpdated.getTime() - b.lastUpdated.getTime();

const newResponsesUrl = RESPONSES_TSV_URL;

/**
 * Loads profile data from Google Sheets, calculates journey completion (sortScore, emojis),
 * then runs getAutoUpdatedMap so `badgesIDs` differ from `submittedBadgeIDs` if auto-claim
 * or restrictions apply.
 */
export interface RawSubmission {
  timestamp: Date;
  /** Owned set after applying this row's added IDs onto prior rows (sheet pastes are add-only). */
  badgeIDs: string[];
  /**
   * Sheet CUSTOM column is exactly `custom` — badges newly added in this row
   * (diff vs previous submission) are system grants, not self-claims.
   */
  isCustomGrant?: boolean;
}

interface PartialProfile {
  email: string;
  name: string;
  lastUpdated: Date;
  badgesIDs: string[];
}

type ProfilesFetchResult = {
  profilesMap: Map<string, Profile>;
  rankedProfiles: Profile[];
  submissionsByEmail: Map<string, RawSubmission[]>;
};

const loadProfilesFromSheet = async (badgesMap: Map<string, Badge>): Promise<ProfilesFetchResult> => {
  const tempMap = new Map<string, PartialProfile>();
  const contributorIndex = buildContributorIndex(badgesMap);
  const subsByEmail = new Map<string, RawSubmission[]>();

  try {
    // Cache-bust so Refresh picks up new sheet rows (browsers/CDN often cache the export).
    const resp = await axios.get(`${newResponsesUrl}&_=${Date.now()}`);
    const lines = resp.data.split('\n');
    lines.shift(); // remove header row

    lines.forEach((line: string) => {
      // Columns: Timestamp | Email | Paste code | CUSTOM
      const cols = line.replace(/\r$/, '').split('\t');
      const timestamp = cols[0];
      const email = cols[1];
      const nameAndBadges = cols[2];
      if (!email || !nameAndBadges) return;

      const lastComma = nameAndBadges.lastIndexOf(',');
      if (lastComma === -1) return;
      const maybeName = nameAndBadges.substring(0, lastComma).trim();
      const addedIDs = parseBadgeString(nameAndBadges.substring(lastComma + 1));
      const existing = tempMap.get(email);
      if (addedIDs.length === 0 && !existing) return;

      const badgeIDs = accumulateOwnedBadgeIDs(existing?.badgesIDs ?? [], addedIDs);
      if (badgeIDs.length === 0) return;

      const isCustomGrant = (cols[3] ?? '').trim().toLowerCase() === 'custom';
      // TSV timestamps are naive London wall-clock, not the viewer's local zone.
      const ts = parseSpreadsheetTimestamp(timestamp);
      if (!subsByEmail.has(email)) subsByEmail.set(email, []);
      subsByEmail.get(email)!.push({ timestamp: ts, badgeIDs, isCustomGrant });

      tempMap.set(email, {
        email,
        name: maybeName || existing?.name || getFirstPartOfEmail(email),
        lastUpdated: ts,
        badgesIDs: badgeIDs,
      });
    });
  } catch (err) {
    console.error('Error fetching Google Sheet data:', err);
    throw err;
  }

  tempMap.forEach((partialProfile, email) => {
    const { creditIds, designIds } = getContributorIds(contributorIndex, email);
    const submittedBadgeIDs = [...new Set([...creditIds, ...designIds])];
    // Drop retired/unknown sheet IDs before auto-claim so they never inflate counts.
    const liveOwned = liveCatalogBadgeIDs(partialProfile.badgesIDs, badgesMap);
    const enabledBadges = new Map(liveOwned.map((id) => [id, true]));
    const autoUpdatedMap = getAutoUpdatedMap(enabledBadges, badgesMap, {
      submittedBadgeIDs,
      email,
    });
    tempMap.set(email, {
      ...partialProfile,
      badgesIDs: [...autoUpdatedMap.entries()].filter(([, isOn]) => isOn).map(([id]) => id),
    });
  });

  const journeyPointsByEmail = new Map<string, number>();
  const journeyEmojisByEmail = new Map<string, string[]>();
  const completionsByJourneyId = new Map(JOURNEYS.map((journey) => [journey.id, 0]));
  const journeyByEmoji = new Map(JOURNEYS.map((journey) => [journey.emoji || '', journey]));

  tempMap.forEach((partialProfile, email) => {
    const owned = new Set(partialProfile.badgesIDs);
    let points = 0;
    const emojis: string[] = [];
    for (const journey of JOURNEYS) {
      if (!isJourneyCompleted(journey, owned)) continue;
      const emoji = journey.emoji || '';
      points += getRarityScoreFromName(journey.rarity);
      emojis.push(emoji);
      completionsByJourneyId.set(journey.id, (completionsByJourneyId.get(journey.id) ?? 0) + 1);
    }

    journeyPointsByEmail.set(email, points);
    journeyEmojisByEmail.set(email, emojis);
  });

  const finalProfiles = Array.from(tempMap.values()).map<Profile>((partialProfile) => {
    const { email, name, lastUpdated, badgesIDs } = partialProfile;

    let badgeScore = 0;
    badgesIDs.forEach((bId) => {
      const b = badgesMap.get(bId);
      if (b) badgeScore += b.rarityScore;
    });

    const journeyScore = journeyPointsByEmail.get(email) || 0;
    const totalScore = badgeScore + journeyScore;

    const finalEmojis = (journeyEmojisByEmail.get(email) || []).sort((a, b) => {
      const journeyA = journeyByEmoji.get(a);
      const journeyB = journeyByEmoji.get(b);
      if (!journeyA || !journeyB) return Number(!!journeyB) - Number(!!journeyA);
      return compareJourneysByClaimScoreAsc(journeyA, journeyB, completionsByJourneyId);
    });

    const { creditIds, designIds } = getContributorIds(contributorIndex, email);

    return {
      email,
      name,
      lastUpdated,
      lastActivityAt: lastUpdated,
      submittedBadgeIDs: [...new Set([...creditIds, ...designIds])],
      badgesIDs: badgesIDs,
      sortScore: totalScore,
      journeyEmojis: finalEmojis,
    };
  });

  finalProfiles.sort(compareProfilesByScore);

  const rankMap = new Map<string, number>();
  finalProfiles.forEach((p, i) => rankMap.set(getFirstPartOfEmail(p.email), i + 1));

  finalProfiles.forEach((profile) => {
    const subs = subsByEmail.get(profile.email);
    if (!subs || subs.length < 2) return;
    subs.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    const recentSubs = subs.filter((s) => s.timestamp >= getFeedUpdatesCutoffDate());
    let previous = subs[subs.length - 2];
    if (recentSubs.length > 2) {
      const oneMonthAgo = subMonths(new Date(), 1);
      const candidates = subs.slice(0, -1);
      previous = candidates.reduce((closest, s) =>
        Math.abs(s.timestamp.getTime() - oneMonthAgo.getTime()) <
        Math.abs(closest.timestamp.getTime() - oneMonthAgo.getTime())
          ? s
          : closest
      );
    }

    const profileId = getFirstPartOfEmail(profile.email);
    const currentRank = rankMap.get(profileId) ?? 0;

    const hypotheticalSortScore = sortScoreForSheetBadgeIDs(previous.badgeIDs, badgesMap, profile);

    const previousRank = rankAmongProfilesExcluding(
      finalProfiles,
      profile,
      hypotheticalSortScore,
      previous.timestamp.getTime()
    );
    const movedUp = previousRank - currentRank;
    if (movedUp > 0 && profile.lastUpdated >= getFeedUpdatesCutoffDate()) {
      profile.positionsMovedUp = movedUp;
    }
  });

  return {
    submissionsByEmail: subsByEmail,
    rankedProfiles: finalProfiles,
    profilesMap: finalProfiles.reduce((acc, p) => acc.set(getFirstPartOfEmail(p.email), p), new Map<string, Profile>()),
  };
};

export function useProfiles(badgesMap: Map<string, Badge>, enabled: boolean) {
  const [profilesMap, setProfilesMap] = useState<Map<string, Profile>>(new Map());
  const [rankedProfiles, setRankedProfiles] = useState<Profile[]>([]);
  const [submissionsByEmail, setSubmissionsByEmail] = useState<Map<string, RawSubmission[]>>(new Map());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [profilesReady, setProfilesReady] = useState(false);
  const [profilesError, setProfilesError] = useState(false);
  const badgesMapRef = useRef(badgesMap);
  badgesMapRef.current = badgesMap;
  const inFlightRef = useRef(false);

  const applyFetchResult = useCallback((result: ProfilesFetchResult) => {
    setSubmissionsByEmail(result.submissionsByEmail);
    setRankedProfiles(result.rankedProfiles);
    setProfilesMap(result.profilesMap);
    setProfilesReady(true);
    setProfilesError(false);
  }, []);

  useEffect(() => {
    // Never fetch profile/submission data before Slack authentication succeeds.
    if (!enabled || badgesMap.size === 0 || profilesReady || inFlightRef.current) return;

    let cancelled = false;
    inFlightRef.current = true;
    loadProfilesFromSheet(badgesMap)
      .then((result) => {
        if (!cancelled) applyFetchResult(result);
      })
      .catch(() => {
        if (!cancelled) setProfilesError(true);
      })
      .finally(() => {
        inFlightRef.current = false;
      });

    return () => {
      cancelled = true;
    };
  }, [badgesMap, enabled, profilesReady, applyFetchResult]);

  const refreshProfiles = useCallback(async () => {
    if (!enabled || badgesMapRef.current.size === 0 || inFlightRef.current) return;
    inFlightRef.current = true;
    setIsRefreshing(true);
    setProfilesError(false);
    try {
      const result = await loadProfilesFromSheet(badgesMapRef.current);
      applyFetchResult(result);
    } catch {
      setProfilesError(true);
    } finally {
      inFlightRef.current = false;
      setIsRefreshing(false);
    }
  }, [enabled, applyFetchResult]);

  return {
    profilesMap,
    rankedProfiles,
    submissionsByEmail,
    refreshProfiles,
    isRefreshing,
    profilesReady,
    profilesError,
  };
}
